import React, { useEffect, useState } from "react"

import { trpc } from "../../lib/trpc"

interface EmailAccount {
  id: string
  email: string
  displayName?: string
  accountType: "oauth_gmail" | "custom_domain" | "smtp_imap"
  provider?: "google"
  inboundDomain?: string
  outboundDomain?: string
  smtpSettings?: string
  signature?: string
  replyTracking: "message_id" | "custom_header" | "reply_to"
  isActive: boolean
  isDefault: boolean
  lastVerifiedAt?: string
  createdAt: string
  updatedAt: string
}

interface SmsAccount {
  id: string
  provider: "twilio"
  accountSid: string
  primaryNumber: string
  messagingServiceSid?: string
  monthlyLimit: number
  currentUsage: number
  billingCycle: "monthly" | "weekly" | "daily"
  webhookUrl?: string
  statusCallbackUrl?: string
  isActive: boolean
  isDefault: boolean
  lastTestedAt?: string
  createdAt: string
  updatedAt: string
}

const AccountTypeBadge: React.FC<{ accountType: string }> = ({ accountType }) => {
  const badges = {
    oauth_gmail: { bg: "bg-red-100 text-destructive dark:bg-red-800 dark:text-red-300", label: "Gmail" },
    custom_domain: {
      bg: "bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-300",
      label: "Custom Domain",
    },
    smtp_imap: { bg: "bg-foregroundx text-gray-800  dark:text-gray-300", label: "SMTP/IMAP" },
  }

  const badge = badges[accountType as keyof typeof badges] || badges.smtp_imap

  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.bg}`}>{badge.label}</span>
}

const StatusBadge: React.FC<{ isActive: boolean; lastVerified?: string }> = ({ isActive, lastVerified }) => {
  if (!isActive) {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-foregroundx text-gray-800  dark:text-gray-300">
        Inactive
      </span>
    )
  }

  const isRecentlyVerified = lastVerified && new Date(lastVerified).getTime() > Date.now() - 24 * 60 * 60 * 1000 // 24 hours

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${
        isRecentlyVerified
          ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300"
          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300"
      }`}
    >
      {isRecentlyVerified ? "Verified" : "Needs Verification"}
    </span>
  )
}

const ProviderBadge: React.FC<{ provider: string }> = ({ provider }) => {
  const badges = {
    twilio: { bg: "bg-red-100 text-destructive dark:bg-red-800 dark:text-red-300", label: "Twilio" },
  }

  const badge = badges[provider as keyof typeof badges] || badges.twilio

  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.bg}`}>{badge.label}</span>
}

const UsageBadge: React.FC<{ current: number; limit: number }> = ({ current, limit }) => {
  const percentage = (current / limit) * 100
  const isHigh = percentage > 80
  const isMedium = percentage > 60

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${
        isHigh
          ? "bg-red-100 text-destructive dark:bg-red-800 dark:text-red-300"
          : isMedium
            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300"
            : "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300"
      }`}
    >
      {current}/{limit} ({percentage.toFixed(0)}%)
    </span>
  )
}

const MessagingAccounts: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"email" | "sms">("email")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null)
  const [accountTypeFilter, setAccountTypeFilter] = useState<string>("")

  // SMS-specific state
  const [showTestModal, setShowTestModal] = useState<string | null>(null)
  const [testMessage, setTestMessage] = useState("")
  const [testPhoneNumber, setTestPhoneNumber] = useState("")
  const [providerFilter, setProviderFilter] = useState<string>("")

  // Query email accounts
  const {
    data: emailAccountsData,
    isLoading: emailLoading,
    refetch: refetchEmail,
  } = trpc.crm.emailAccounts.list.useQuery({
    accountType: accountTypeFilter ? (accountTypeFilter as any) : undefined,
    limit: 50,
    offset: 0,
  })

  // Query SMS accounts
  const {
    data: smsAccountsData,
    isLoading: smsLoading,
    refetch: refetchSms,
  } = trpc.crm.smsAccounts.list.useQuery({
    provider: providerFilter ? (providerFilter as any) : undefined,
    limit: 50,
    offset: 0,
  })

  // Email mutations
  const testEmailConnectionMutation = trpc.crm.emailAccounts.testConnection.useMutation({
    onSuccess: (result) => {
      alert(`Connection Test: ${result.status}\n${result.message}`)
      // @ts-ignore TODO: Fix scope issue with refetchEmail
      void refetchEmail()
    },
  })

  const deleteEmailAccountMutation = trpc.crm.emailAccounts.delete.useMutation({
    onSuccess: () => {
      // @ts-ignore TODO: Fix scope issue with refetchEmail
      void refetchEmail()
    },
  })

  const createEmailAccountMutation = trpc.crm.emailAccounts.create.useMutation({
    onSuccess: () => {
      setShowCreateModal(false)
      // @ts-ignore TODO: Fix scope issue with refetchEmail
      void refetchEmail()
    },
  })

  // SMS mutations
  const testSmsConnectionMutation = trpc.crm.smsAccounts.testConnection.useMutation({
    onSuccess: (result) => {
      alert(`Connection Test: ${result.success ? "Success" : "Failed"}\n${result.message}`)
      void refetchSms()
    },
  })

  const sendTestSmsMutation = trpc.crm.smsAccounts.sendTestSMS.useMutation({
    onSuccess: (result) => {
      alert(`Test SMS: ${result.success ? "Sent" : "Failed"}\nMessage ID: ${result.messageId}\n${result.message}`)
      setShowTestModal(null)
      setTestMessage("")
      setTestPhoneNumber("")
      void refetchSms()
    },
    onError: (error) => {
      alert(`Failed to send test SMS: ${error.message}`)
    },
  })

  const deleteSmsAccountMutation = trpc.crm.smsAccounts.delete.useMutation({
    onSuccess: () => {
      void refetchSms()
    },
  })

  // Gmail OAuth mutations
  const [isGmailAuthLoading, setIsGmailAuthLoading] = useState(false)
  const utils = trpc.useUtils()
  const exchangeGmailCodeMutation = trpc.gmailOauth.exchangeCode.useMutation({
    onSuccess: (result) => {
      alert(`Gmail OAuth ${result.action}: ${result.email}`)
      // @ts-ignore TODO: Fix scope issue with refetchEmail
      void refetchEmail()
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname)
    },
    onError: (error) => {
      alert(`Gmail OAuth failed: ${error.message}`)
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname)
    },
  })
  const gmailTestConnectionMutation = trpc.gmailOauth.testConnection.useMutation({
    onSuccess: (result) => {
      if (result.success) {
        alert(`Gmail Test: ${result.message}`)
      } else {
        alert(`Gmail Test Failed: ${result.message}`)
      }
      // @ts-ignore TODO: Fix scope issue with refetchEmail
      void refetchEmail()
    },
  })

  // Handle OAuth callback on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get("code")
    const state = urlParams.get("state")
    const error = urlParams.get("error")

    if (error) {
      alert(`Gmail OAuth error: ${error}`)
      window.history.replaceState({}, document.title, window.location.pathname)
      return
    }

    if (code) {
      const currentUrl = window.location.origin
      const redirectUri = `${currentUrl}/crm/email-accounts`

      exchangeGmailCodeMutation.mutate({
        code,
        redirectUri,
        state: state || undefined,
      })
    }
  }, [])

  const handleTestConnection = (account: EmailAccount) => {
    if (account.accountType === "oauth_gmail") {
      gmailTestConnectionMutation.mutate({ accountId: account.id })
    } else {
      testEmailConnectionMutation.mutate({ id: account.id })
    }
  }

  const handleGmailOAuth = async () => {
    try {
      setIsGmailAuthLoading(true)
      const currentUrl = window.location.origin
      const redirectUri = `${currentUrl}/crm/email-accounts`

      // Use utils.fetch to call the query imperatively
      const result = await utils.gmailOauth.getAuthUrl.fetch({
        redirectUri,
      })

      // Redirect to Google OAuth
      window.location.href = result.authUrl
    } catch (error) {
      alert("Failed to start Gmail OAuth: " + (error as Error).message)
      setIsGmailAuthLoading(false)
    }
  }

  const handleDeleteAccount = (account: EmailAccount) => {
    if (confirm(`Are you sure you want to delete the email account ${account.email}?`)) {
      deleteEmailAccountMutation.mutate({ id: account.id })
    }
  }

  const handleCreateAccount = (formData: FormData) => {
    const accountType = formData.get("accountType") as string
    const email = formData.get("email") as string
    const displayName = formData.get("displayName") as string
    const isDefault = formData.get("isDefault") === "true"

    if (!email || !accountType) {
      alert("Email and account type are required")
      return
    }

    const baseData = {
      email,
      displayName: displayName || undefined,
      accountType: accountType as any,
      isDefault,
    }

    if (accountType === "smtp_imap") {
      const host = formData.get("smtpHost") as string
      const port = parseInt(formData.get("smtpPort") as string)
      const username = formData.get("smtpUsername") as string
      const password = formData.get("smtpPassword") as string
      const secure = formData.get("smtpSecure") === "true"

      if (!host || !port || !username || !password) {
        alert("All SMTP settings are required")
        return
      }

      // @ts-ignore TODO: Fix scope issue with mutations
      createEmailAccountMutation.mutate({
        ...baseData,
        smtpSettings: { host, port, username, password, secure },
      })
    } else if (accountType === "custom_domain") {
      const inboundDomain = formData.get("inboundDomain") as string
      const outboundDomain = formData.get("outboundDomain") as string

      // @ts-ignore TODO: Fix scope issue with mutations
      createEmailAccountMutation.mutate({
        ...baseData,
        inboundDomain: inboundDomain || undefined,
        outboundDomain: outboundDomain || undefined,
      })
    } else if (accountType === "oauth_gmail") {
      // Start Gmail OAuth flow instead of creating account directly
      setShowCreateModal(false)
      void handleGmailOAuth()
    } else {
      // Other account types
      // @ts-ignore TODO: Fix scope issue with mutations
      createEmailAccountMutation.mutate(baseData)
    }
  }

  const handleSendTestSms = (accountId: string) => {
    if (!testMessage.trim() || !testPhoneNumber.trim()) {
      alert("Please enter both phone number and message")
      return
    }

    sendTestSmsMutation.mutate({
      id: accountId,
      to: testPhoneNumber,
      message: testMessage,
    })
  }

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold  dark:text-white">Messaging Accounts</h1>
              <p className="mt-2  dark:text-gray-400">Manage your email and SMS accounts for outbound messaging</p>
            </div>
            <div className="flex gap-2">
              {activeTab === "email" && (
                <>
                  <button
                    onClick={handleGmailOAuth}
                    disabled={isGmailAuthLoading}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <span>📧</span>
                    {isGmailAuthLoading ? "Connecting..." : "Connect Gmail"}
                  </button>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <span>+</span>
                    Add Email Account
                  </button>
                </>
              )}
              {activeTab === "sms" && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <span>+</span>
                  Add SMS Account
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-border dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("email")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "email"
                    ? "border-blue-500  dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                📧 Email Accounts
              </button>
              <button
                onClick={() => setActiveTab("sms")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "sms"
                    ? "border-blue-500  dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                📱 SMS Accounts
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <select
            value={accountTypeFilter}
            onChange={(e) => setAccountTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
          >
            <option value="">All Account Types</option>
            <option value="oauth_gmail">Gmail</option>
            <option value="custom_domain">Custom Domain</option>
            <option value="smtp_imap">SMTP/IMAP</option>
          </select>
        </div>

        {/* Email Accounts List */}
        <div className="bg-background  rounded-lg shadow-sm border border-border dark:border-gray-700">
          {emailLoading ? (
            <div className="p-8 text-center">
              <div className="text-gray-500">Loading email accounts...</div>
            </div>
          ) : emailAccountsData?.accounts && emailAccountsData.accounts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-foregroundx dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email Account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Default
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Verified
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {/* @ts-ignore TODO: Fix scope issue with emailAccountsData */}
                  {emailAccountsData.accounts.map((account) => (
                    <tr key={account.id} className=" dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium  dark:text-white">{account.displayName || account.email}</div>
                          {account.displayName && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{account.email}</div>
                          )}
                          {account.signature && <div className="text-xs text-gray-400 mt-1">Has signature</div>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <AccountTypeBadge accountType={account.accountType} />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge
                          isActive={account.isActive}
                          lastVerified={
                            account.lastVerifiedAt ? new Date(account.lastVerifiedAt).toISOString() : undefined
                          }
                        />
                      </td>
                      <td className="px-6 py-4">
                        {account.isDefault && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300">
                            Default
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {account.lastVerifiedAt ? new Date(account.lastVerifiedAt).toLocaleDateString() : "Never"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* @ts-ignore TODO: Fix scope issue with mutations */}
                          <button
                            onClick={() =>
                              handleTestConnection({
                                ...account,
                                lastVerifiedAt:
                                  account.lastVerifiedAt instanceof Date
                                    ? account.lastVerifiedAt.toISOString()
                                    : account.lastVerifiedAt,
                                createdAt:
                                  account.createdAt instanceof Date
                                    ? account.createdAt.toISOString()
                                    : account.createdAt,
                                updatedAt:
                                  account.updatedAt instanceof Date
                                    ? account.updatedAt.toISOString()
                                    : account.updatedAt,
                              } as EmailAccount)
                            }
                            disabled={testEmailConnectionMutation.isLoading || gmailTestConnectionMutation.isLoading}
                            className=" hover:text-blue-800 text-sm disabled:opacity-50"
                          >
                            {/* @ts-ignore TODO: Fix scope issue with mutations */}
                            {testEmailConnectionMutation.isLoading || gmailTestConnectionMutation.isLoading
                              ? "Testing..."
                              : "Test"}
                          </button>
                          <button
                            onClick={() =>
                              setEditingAccount({
                                ...account,
                                lastVerifiedAt:
                                  account.lastVerifiedAt instanceof Date
                                    ? account.lastVerifiedAt.toISOString()
                                    : account.lastVerifiedAt,
                                createdAt:
                                  account.createdAt instanceof Date
                                    ? account.createdAt.toISOString()
                                    : account.createdAt,
                                updatedAt:
                                  account.updatedAt instanceof Date
                                    ? account.updatedAt.toISOString()
                                    : account.updatedAt,
                              } as EmailAccount)
                            }
                            className=" hover:text-gray-800 text-sm"
                          >
                            Edit
                          </button>
                          {/* @ts-ignore TODO: Fix scope issue with mutations */}
                          <button
                            onClick={() =>
                              handleDeleteAccount({
                                ...account,
                                lastVerifiedAt:
                                  account.lastVerifiedAt instanceof Date
                                    ? account.lastVerifiedAt.toISOString()
                                    : account.lastVerifiedAt,
                                createdAt:
                                  account.createdAt instanceof Date
                                    ? account.createdAt.toISOString()
                                    : account.createdAt,
                                updatedAt:
                                  account.updatedAt instanceof Date
                                    ? account.updatedAt.toISOString()
                                    : account.updatedAt,
                              } as EmailAccount)
                            }
                            disabled={deleteEmailAccountMutation.isLoading}
                            className="text-destructive hover:text-destructive text-sm disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-500 mb-4">No email accounts configured</div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Your First Email Account
              </button>
            </div>
          )}
        </div>

        {/* Create Account Modal */}
        {showCreateModal && (
          <CreateAccountModal
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateAccount}
            isLoading={createEmailAccountMutation.isLoading}
          />
        )}

        {/* Edit Account Modal */}
        {editingAccount && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Edit Email Account</h2>
              <div className="text-center py-8">
                <div className="text-gray-500 mb-4">Edit functionality coming soon!</div>
                <button
                  onClick={() => setEditingAccount(null)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const CreateAccountModal: React.FC<{
  onClose: () => void
  onCreate: (formData: FormData) => void
  isLoading: boolean
}> = ({ onClose, onCreate, isLoading }) => {
  const [accountType, setAccountType] = useState<string>("oauth_gmail")

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background  rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold  dark:text-white mb-4">Add Email Account</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onCreate(new FormData(e.currentTarget))
          }}
        >
          <div className="space-y-4">
            {/* Account Type */}
            <div>
              <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Account Type</label>
              <select
                name="accountType"
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
              >
                <option value="oauth_gmail">Gmail (OAuth)</option>
                <option value="custom_domain">Custom Domain</option>
                <option value="smtp_imap">SMTP/IMAP</option>
              </select>
            </div>

            {/* Email and Display Name */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                  Email Address *
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Display Name</label>
                <input
                  name="displayName"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                  placeholder="Your Name"
                />
              </div>
            </div>

            {/* Account Type Specific Fields */}
            {accountType === "smtp_imap" && (
              <div className="space-y-4 border-t pt-4">
                <div className="text-sm font-medium text-primary dark:text-gray-300">SMTP Configuration</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      SMTP Host *
                    </label>
                    <input
                      name="smtpHost"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="smtp.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Port *</label>
                    <input
                      name="smtpPort"
                      type="number"
                      defaultValue="587"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Username *</label>
                    <input
                      name="smtpUsername"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Username or email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Password *</label>
                    <input
                      name="smtpPassword"
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Password or app password"
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    name="smtpSecure"
                    type="checkbox"
                    defaultChecked
                    value="true"
                    className="rounded border-gray-300  shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200/50"
                  />
                  <label className="ml-2 block text-sm text-primary dark:text-gray-300">Use SSL/TLS</label>
                </div>
              </div>
            )}

            {accountType === "custom_domain" && (
              <div className="space-y-4 border-t pt-4">
                <div className="text-sm font-medium text-primary dark:text-gray-300">Domain Configuration</div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Inbound Domain
                    </label>
                    <input
                      name="inboundDomain"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="mail.yourdomain.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Outbound Domain
                    </label>
                    <input
                      name="outboundDomain"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="send.yourdomain.com"
                    />
                  </div>
                </div>
              </div>
            )}

            {accountType === "oauth_gmail" && (
              <div className="border-t pt-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Note:</strong> Gmail OAuth will redirect you to Google to authenticate your account. The
                    email field above will be ignored - your Gmail address will be automatically detected.
                  </div>
                </div>
              </div>
            )}

            {/* Default Account */}
            <div className="flex items-center">
              <input
                name="isDefault"
                type="checkbox"
                value="true"
                className="rounded border-gray-300  shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200/50"
              />
              <label className="ml-2 block text-sm text-primary dark:text-gray-300">Set as default email account</label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Adding..." : "Add Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MessagingAccounts
